"use client";

/**
 * THE JOIN GATE'S SETUP BRANCH, as the hook `BillingGate` consumes (spec
 * underwriter/2026-10-17-001 R7, D29, D44, amended G42).
 *
 * A row billed on a rail of its own with a plan page has a setup surface: the
 * page an admin is taken to when staff applied an automatic setup that is not
 * paid yet, and the setup surfaces that never wall (the plan page, the checkout
 * return, the billing page). All from the registry row.
 *
 * A SETUP FLOOR is a path the walls exempt (a paid-rungs row's floor) that is
 * NOT a setup surface: the setup branch still runs there, and only it. Every
 * other exempt path paints exactly as before, ahead of any loading guard.
 *
 * The hook owns the gate-status read (fetched on a setup floor too, D29, so a
 * pending setup reaches its admin wherever in the product they land), the
 * decision, and the fourth redirect effect, which emits
 * `billing_setup_gate_redirected` once per redirect (D44; scope and role only).
 */
import { c as _c } from "react/compiler-runtime";
import { useEffect, useEffectEvent, useRef } from "react";
import { useRouter } from "next/navigation";
import { getEvent, useAnalytics } from "@/lib/analytics";
import { paymentSetupSurfaceOf, type PlatformScope } from "@/domain/scope/platform-scope";
import { isSetupExemptPath, setupGateOf, setupRedirectTarget } from "./setup-gate";
import { useBillingGateStatus } from "./use-billing-gate-status";
export interface UseSetupGateInput {
  readonly pathname: string;
  readonly surfaceScope: PlatformScope;
  /** The walls' own exemption, computed by the gate before this branch. */
  readonly isExempt: boolean;
  /** Identity resolved and signed in. */
  readonly signedIn: boolean;
  readonly isAdmin: boolean;
  readonly skipGates: boolean;
  readonly holdWallRedirects: boolean;
}
export function useSetupGate(t0) {
  const $ = _c(25);
  const {
    pathname,
    surfaceScope,
    isExempt,
    signedIn,
    isAdmin,
    skipGates,
    holdWallRedirects
  } = t0;
  const router = useRouter();
  const {
    track
  } = useAnalytics();
  const setupSurface = paymentSetupSurfaceOf(surfaceScope);
  const setupExempt = isSetupExemptPath(pathname, setupSurface);
  const setupFloor = isExempt && setupSurface !== null && !setupExempt;
  const shouldCheckBilling = signedIn && !skipGates && (!isExempt || setupFloor);
  const {
    data: gateStatus,
    isLoading: gateLoading
  } = useBillingGateStatus(shouldCheckBilling);
  const setupDecision = setupGateOf({
    pathname,
    surface: setupSurface,
    gateStatus,
    isAdmin,
    skipGates
  });
  let t1;
  if ($[0] !== surfaceScope || $[1] !== track) {
    t1 = () => {
      track(getEvent("billing-setup", "billing_setup_gate_redirected"), {
        scope: surfaceScope,
        role: "admin"
      });
    };
    $[0] = surfaceScope;
    $[1] = track;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onRedirected = useEffectEvent(t1);
  const redirectTarget = setupSurface === null ? null : setupRedirectTarget(setupSurface);
  const trackedFrom = useRef(null);
  let t2;
  if ($[3] !== holdWallRedirects || $[4] !== isAdmin || $[5] !== onRedirected || $[6] !== pathname || $[7] !== redirectTarget || $[8] !== router || $[9] !== setupDecision || $[10] !== skipGates) {
    t2 = () => {
      if (isAdmin && !skipGates && !holdWallRedirects && setupDecision === "redirect" && redirectTarget !== null) {
        router.push(redirectTarget);
        if (trackedFrom.current !== pathname) {
          trackedFrom.current = pathname;
          onRedirected();
        }
      }
    };
    $[3] = holdWallRedirects;
    $[4] = isAdmin;
    $[5] = onRedirected;
    $[6] = pathname;
    $[7] = redirectTarget;
    $[8] = router;
    $[9] = setupDecision;
    $[10] = skipGates;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== holdWallRedirects || $[13] !== isAdmin || $[14] !== pathname || $[15] !== redirectTarget || $[16] !== router || $[17] !== setupDecision || $[18] !== skipGates) {
    t3 = [isAdmin, skipGates, holdWallRedirects, setupDecision, redirectTarget, pathname, router];
    $[12] = holdWallRedirects;
    $[13] = isAdmin;
    $[14] = pathname;
    $[15] = redirectTarget;
    $[16] = router;
    $[17] = setupDecision;
    $[18] = skipGates;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  useEffect(t2, t3);
  let t4;
  if ($[20] !== gateLoading || $[21] !== gateStatus || $[22] !== setupDecision || $[23] !== setupFloor) {
    t4 = {
      setupFloor,
      setupDecision,
      gateStatus,
      gateLoading
    };
    $[20] = gateLoading;
    $[21] = gateStatus;
    $[22] = setupDecision;
    $[23] = setupFloor;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  return t4 as const;
}